import React from 'react';
import clsx from 'clsx';

export function randomId() {
  return `${Math.random().toString(36).substr(2, 9)}`;
}

export function useMediaQuery(query) {
  const getMatches = (query) => {
    // Prevents SSR issues
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  };

  const [matches, setMatches] = React.useState(getMatches(query));

  function handleChange() {
    setMatches(getMatches(query));
  }

  React.useEffect(() => {
    const matchMedia = window.matchMedia(query);

    // Triggered at the first client-side load and if query changes
    handleChange();

    // Listen matchMedia
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener('change', handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener('change', handleChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return matches;
}

export function useAudioLevel(localAudioTrack, sampleInterval = 250) {
  const [level, setLevel] = React.useState(0);
  const audioCtxRef = React.useRef(null);
  const analyserRef = React.useRef(null);
  const dataRef = React.useRef(null);

  React.useEffect(() => {
    if (!localAudioTrack) return;

    const mediaStreamTrack =
      localAudioTrack.mediaStreamTrack || localAudioTrack.track;
    if (!mediaStreamTrack) return;

    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    const data = new Uint8Array(analyser.frequencyBinCount);

    const src = audioCtx.createMediaStreamSource(
      new MediaStream([mediaStreamTrack])
    );
    src.connect(analyser);

    audioCtxRef.current = audioCtx;
    analyserRef.current = analyser;
    dataRef.current = data;

    const intervalId = setInterval(() => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      setLevel(Math.sqrt(sum / data.length));
    }, sampleInterval);

    return () => {
      clearInterval(intervalId);
      audioCtx.close();
    };
  }, [localAudioTrack, sampleInterval]);

  return level; // 0.0 (silence) … ~1.0 (max)
}

export function chain() {
  const callbacks = Array.from(arguments);
  return function () {
    const args = Array.from(arguments);
    for (const callback of callbacks) {
      if (typeof callback === 'function') {
        try {
          callback.apply(null, args);
        } catch (e) {
          console.error(e);
        }
      }
    }
  };
}

export function mergePropsReactAria(...args) {
  // Start with a base clone of the first argument. This is a lot faster than starting
  // with an empty object and adding properties as we go.
  const result = { ...args[0] };
  for (let i = 1; i < args.length; i++) {
    const props = args[i];
    for (const key in props) {
      const a = result[key];
      const b = props[key];

      // Chain events
      if (
        typeof a === 'function' &&
        typeof b === 'function' &&
        // This is a lot faster than a regex.
        key[0] === 'o' &&
        key[1] === 'n' &&
        key.charCodeAt(2) >= /* 'A' */ 65 &&
        key.charCodeAt(2) <= /* 'Z' */ 90
      ) {
        result[key] = chain(a, b);

        // Merge classnames, sometimes classNames are empty string which eval to false, so we just need to do a type check
      } else if (
        (key === 'className' || key === 'UNSAFE_className') &&
        typeof a === 'string' &&
        typeof b === 'string'
      ) {
        result[key] = clsx(a, b);
      } else {
        result[key] = b !== undefined ? b : a;
      }
    }
  }
  return result;
}

export function isProp(prop) {
  return prop !== undefined;
}

export function mergeProps(...props) {
  return mergePropsReactAria(...props.filter(isProp));
}

/**
 * Formats seconds into a time string in the format h:mm:ss or mm:ss
 * If hours is 0, it will be omitted (mm:ss)
 * Minutes and seconds will always have leading zeros when less than 10
 *
 * @param {number} seconds - Number of seconds to format
 * @returns {string} Formatted time string
 */
export function formatTime(seconds) {
  const totalSeconds = Math.round(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } else {
    // Always show at least minutes and seconds (0:xx) even when minutes are zero
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}

/**
 * Converts a number of seconds into a human-readable duration string.
 * Examples:
 *   75    -> "1m 15s"
 *   3605  -> "1h 0m 5s"
 *   3663  -> "1h 1m 3s"
 * @param {number} seconds - total seconds
 * @returns {string} formatted duration
 */
export function formatDuration(seconds) {
  const secs = Math.max(0, Math.floor(seconds));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  // always show seconds if no hours/minutes or if s > 0
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);
  return parts.join(' ');
}


export function getMediaAuthToken(spToken) {
  const parts = spToken.split('.');
  if (parts.length == 3) {
    return `tok_${parts[2]}`;
  } else {
    throw new Error('Invalid JWT token');
  }
}

/**
 * Calculate the number of seconds between two dates
 *
 * @param {string|Date|number} startDate - The start date (can be Date object, ISO string, or timestamp)
 * @param {string|Date|number} endDate - The end date (can be Date object, ISO string, or timestamp)
 * @param {boolean} [absolute=true] - Whether to return the absolute value (positive number)
 * @returns {number} The number of seconds between the dates
 */
export function getSecondsBetweenDates(startDate, endDate, absolute = true) {
  // Handle different input types
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  // Calculate difference in milliseconds
  const diffMs = end.getTime() - start.getTime();

  // Convert to seconds
  const diffSeconds = diffMs / 1000;

  // Return absolute value if requested
  return absolute ? Math.abs(diffSeconds) : diffSeconds;
}
